import { internationalToPolish } from './chord-operations';

export const CHORD_REGEX = /([A-H]m?(#|is|b)?|[a-h](#|is)?)(\d{1,2})?(m|M|min|maj|dim|aug|sus|add)?(\d{1,2})?/;

export const getLocalizedKey = (key: string, notation: string): string =>
  notation === 'polish' ? (internationalToPolish[key] || key) : key;

interface Chord {
  chord: string;
  index: number;
  prefix: string;
  suffix: string;
}

interface Line {
  type: 'chord' | 'text';
  content: Chord[] | string;
  lineNumber?: number;
}

interface Song {
  lines: Line[];
}

export class LineProcessor {
  private chordOnly: RegExp;
  private chordAboveLinePattern: string;
  private chordInlined: string;

  constructor() {
    this.chordOnly = CHORD_REGEX;
    this.chordAboveLinePattern = `(?<![\\p{L}])(${this.chordOnly.source})(?![\\p{L}])`;
    this.chordInlined = `\\[(${this.chordOnly.source})\\]`;
  }

  parseChordsTxt(content: unknown, preserveSourceLines = false): Song {
    const song: Song = { lines: [] };
    if (typeof content !== 'string') {
      console.warn('Invalid content type:', typeof content);
      return song;
    }
    try {
      const lines = content.split('\n').map(line => line.normalize('NFC'));

      for (const [index, line] of lines.entries()) {
        for (const processedLine of this.processLine(line)) {
          if (preserveSourceLines) processedLine.lineNumber = index + 1;
          song.lines.push(processedLine);
        }
      }
    } catch (e) {
      console.error('Error parsing chords:', e, content);
      return song;
    }
    return song;
  }

  private processMixedLines(line: string): Line[] | null {
    const chordInlineMatches = [...line.matchAll(new RegExp(this.chordInlined, 'g'))];
    const icountInlined = chordInlineMatches.length;
    const nonChordsCount = line.replace(new RegExp(this.chordInlined, 'g'), '').trim().split(' ').length;

    if (icountInlined === 0 || nonChordsCount === 0) return null;

    let chords: Chord[] = [];
    let resultLine = '';
    let prevEnd = 0;
    let prevLen = 0;

    chordInlineMatches.forEach(match => {
      resultLine += line.slice(prevEnd, match.index);
      chords.push({
        chord: match[1],
        index: resultLine.length,
        prefix: ' '.repeat(resultLine.length - prevLen),
        suffix: ''
      });
      prevLen = resultLine.length;
      prevEnd = match.index! + match[0].length;
    });

    resultLine += line.slice(prevEnd);
    return [
      { type: 'chord', content: chords },
      { type: 'text', content: resultLine }
    ];
  }

  private processLine(line: string): Line[] {
    let mixedLines = this.processMixedLines(line);
    if (mixedLines) {
      return mixedLines;
    } else {
      let aboveLinePatterResult = line.replace(/[^\p{L}\d#]+/gu, ' ').split(' ');
      let words = aboveLinePatterResult.filter(element => !new RegExp(this.chordAboveLinePattern, 'gu').test(element)).length;
      let cords = aboveLinePatterResult.filter(element => new RegExp(this.chordAboveLinePattern, 'gu').test(element)).length;

      if (words > 2 && words - cords >= 0) {
        return [{ type: 'text', content: line.replace(/\n|\r/g, '') }];
      }

      let chords: Chord[] = [];
      let prevEnd = 0;
      let chordInlineMatches = [...line.matchAll(new RegExp(this.chordAboveLinePattern, 'gu'))];
      chordInlineMatches.forEach(match => {
        const chordIndex = match.index! + match[0].length - match[1].length;
        const prefix = line.slice(prevEnd, match.index);

        const currentChord: Chord = {
          chord: match[1],
          index: chordIndex,
          prefix: prefix,
          suffix: ''
        };

        // If there's a previous chord, move trailing decorations from current prefix to previous suffix
        // This handles cases like (E) (Fis) where the closing parenthesis of (E)
        // ends up in the prefix of (Fis).
        if (chords.length > 0) {
          const prev = chords[chords.length - 1];
          // Pattern for closing decorations that should belong to the previous chord:
          // Closing brackets/braces/parentheses, repetition markers, or trailing slashes.
          const decorationMatch = currentChord.prefix.match(/^(\s*[)\]}]|\s*\/x\d+|\s*\/\d+|\s*\/)+/);
          if (decorationMatch) {
            prev.suffix += decorationMatch[0];
            currentChord.prefix = currentChord.prefix.slice(decorationMatch[0].length);
          }
        }

        chords.push(currentChord);
        prevEnd = match.index! + match[0].length;
      });

      if (cords === 0 && chords.length === 0) {
        return [{ type: 'text', content: line.replace(/\n|\r/g, '') }];
      }

      if (chords.length > 0) {
        chords[chords.length - 1].suffix = line.slice(prevEnd).replace(/\n|\r/g, '');
      }

      return [{ type: 'chord', content: chords }];
    }
  }
}
